import { NotificationCount } from "@/components/notification-count/NotificationCount.tsx";
import { Search } from "@/components/search/Search.tsx";
import { useDebounceUpdate } from "@/hooks/use-debounce-update.jook.ts";
import useCustomFieldsHook from "@/hooks/use-custom-fields.hook.tsx";
import { parseUrlAppHelper } from "@/helpers/parse-url.helper.ts";
import {
  parseUrlHelper,
  Button,
  ConfirmationModal,
  DropdownMenu,
  DropdownMenuItem,
  FontAwesomeIcon,
  Page,
  PaginationBlock,
  SplitButton,
  Stack,
  Table,
  useModals,
  ExpandableText,
} from "@skailer-com/uikit";
import { EmployeeCustomTableRowsFilterModal } from "@/pages/settings/custom-fields/widgets/EmployeeCustomTableRowsFilterModal.tsx";
import { useTableRows } from "@/queries/use-custom-fields.query.ts";
import { useEmployeeCustomTableRowDelete } from "@/queries/employees/use-employee-custom-table.query.ts";
import { useAppConfig } from "@/queries/settings/use-app.query.ts";
import { ColumnSizes } from "@/types/common.types.ts";
import {
  ApiCFEmployeeTableRow,
  ApiCFTableFieldFilter,
  ApiCFTableRowFilter,
  ApiCustomField,
  ApiFieldTypes,
  ApiFieldValueTypes,
  ApiTable,
  CFTableRowSortColumn,
} from "@/types/custom-field.types.ts";
import { ApiSortingDirection } from "@/types/api.types.ts";
import { ApiEmployeeLifecycleStatus } from "@/types/employees/employee.types.ts";

const DEFAULT_LIFECYCLE_STATUSES: ApiEmployeeLifecycleStatus[] = [
  ApiEmployeeLifecycleStatus.hired,
  ApiEmployeeLifecycleStatus.employed,
  ApiEmployeeLifecycleStatus.on_leave,
  ApiEmployeeLifecycleStatus.on_parental_leave,
];
import { apiCustomFields } from "@/api/custom-fields.api.ts";
import { useTitle } from "@/hooks/use-title.hook.ts";
import { useDownloadHelper } from "@/hooks/use-download.helper.ts";
import usePermissions from "@/hooks/use-permissions.hook.ts";
import NotFound from "@/pages/NotFound.tsx";
import { ApiPermission } from "@/types/role.types.ts";
import { ColumnDef, createColumnHelper } from "@tanstack/react-table";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useSearchParams } from "react-router-dom";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import { EmployeeItem } from "@/components/employee/EmployeeItem.tsx";
import { EmployeeHoverCard } from "@/components/employee/EmployeeHoverCard.tsx";
import { EmployeeCustomTableRecordModal } from "@/pages/employees/employee/widgets/EmployeeCustomTableRecordModal.tsx";
import { isCompensationRollupField } from "@/utils/compensation-custom-field.utils.ts";
import { getEmployeePositionTitle } from "@/types/employees/employee-position.types.ts";

function parseFieldFilters(raw: string | null): ApiCFTableFieldFilter[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw, (key, value) => {
      if (
        (key.endsWith("_from") || key.endsWith("_to")) &&
        typeof value === "string" &&
        /^\d{4}-\d{2}-\d{2}/.test(value)
      ) {
        const d = new Date(value);
        return isNaN(d.getTime()) ? value : d;
      }
      return value;
    });
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function parseSortingFromParam(raw: string | null) {
  if (!raw) {
    return {
      fields: [{ field: CFTableRowSortColumn.employee, direction: ApiSortingDirection.asc }],
    };
  }
  return {
    fields: raw.split(",").map((token) => {
      const [field, direction] = token.split(":");
      return {
        field,
        direction: (direction as ApiSortingDirection) ?? ApiSortingDirection.asc,
      };
    }),
  };
}

export function EmployeeCustomTableRows() {
  const { t } = useTranslation();
  const { table_slug } = useParams();
  const tableSlug = table_slug ? String(table_slug) : undefined;
  const [searchParams, setSearchParams] = useSearchParams();
  const { getFieldTitle, renderFieldDataInCell } = useCustomFieldsHook();
  const { renderDate } = useFormatter();
  const { openModal } = useModals();
  const { canIEdit, canManageCustomTableForEmployee, canViewCustomTableChangedByForEmployee } =
    usePermissions();
  const { downloadFile } = useDownloadHelper();

  const { data: config } = useAppConfig();

  const table = useMemo<ApiTable | undefined>(() => {
    if (!tableSlug) return undefined;
    const tabs = config?.ui?.employee_profile?.tabs ?? [];
    for (const tab of tabs) {
      for (const item of tab.items) {
        if (item.table && item.table.slug === tableSlug) return item.table;
      }
    }
    return undefined;
  }, [config, tableSlug]);

  useTitle(table?.title ?? t("custom_fields:settings_title"), t("common:settings"));

  const filter = useMemo<ApiCFTableRowFilter>(() => {
    const lifecycleStatuses = parseUrlAppHelper.getEnumArray(
      searchParams,
      "employee_lifecycle_statuses",
      ApiEmployeeLifecycleStatus,
      DEFAULT_LIFECYCLE_STATUSES
    );

    return {
      page_number: parseUrlHelper.getNumber(searchParams, "page"),
      text: searchParams.get("search") ?? undefined,
      employee_ids: parseUrlHelper.getNumberArray(searchParams, "employee_ids"),
      job_profile_ids: parseUrlHelper.getNumberArray(searchParams, "job_profile_ids"),
      job_role_ids: parseUrlHelper.getNumberArray(searchParams, "job_role_ids"),
      department_ids: parseUrlHelper.getNumberArray(searchParams, "department_ids"),
      division_ids: parseUrlHelper.getNumberArray(searchParams, "division_ids"),
      location_ids: parseUrlHelper.getNumberArray(searchParams, "location_ids"),
      employee_lifecycle_statuses: lifecycleStatuses,
      date_effective_from: parseUrlAppHelper.getDate(searchParams, "date_effective_from"),
      date_effective_to: parseUrlAppHelper.getDate(searchParams, "date_effective_to"),
      field_filters: parseFieldFilters(searchParams.get("field_filters")),
      sorting: parseSortingFromParam(searchParams.get("sorting")),
    } as ApiCFTableRowFilter;
  }, [searchParams]);

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if ((filter.employee_ids?.length ?? 0) > 0) count++;
    if ((filter.job_profile_ids?.length ?? 0) > 0) count++;
    if ((filter.job_role_ids?.length ?? 0) > 0) count++;
    if ((filter.department_ids?.length ?? 0) > 0) count++;
    if ((filter.division_ids?.length ?? 0) > 0) count++;
    if ((filter.location_ids?.length ?? 0) > 0) count++;
    if (filter.date_effective_from || filter.date_effective_to) count++;
    if ((filter.field_filters?.length ?? 0) > 0) count += filter.field_filters!.length;
    return count;
  }, [filter]);

  const [searchTerm, setSearchTerm] = useState<string | undefined>(filter.text);

  const [syncedFilterText, setSyncedFilterText] = useState(filter.text);
  if (filter.text !== syncedFilterText) {
    setSyncedFilterText(filter.text);
    setSearchTerm(filter.text);
  }

  useDebounceUpdate({
    data: searchTerm,
    timeout: 300,
    execute: (newData) => {
      setSearchParams((current) => {
        if (newData) current.set("search", newData);
        else current.delete("search");
        current.set("page", "1");
        return current;
      });
    },
  });

  const rowDelete = useEmployeeCustomTableRowDelete();

  const handleEdit = useCallback(
    (row: ApiCFEmployeeTableRow) => {
      if (!table) return;
      if (!canManageCustomTableForEmployee(row.employee, table.id)) return;

      openModal(EmployeeCustomTableRecordModal, {
        employeeId: row.employee.id,
        table,
        row,
      });
    },
    [table, canManageCustomTableForEmployee, openModal]
  );

  const handleDeleteAction = useCallback(
    (row: ApiCFEmployeeTableRow) => {
      if (!table) return;
      if (!canManageCustomTableForEmployee(row.employee, table.id)) return;

      openModal(ConfirmationModal, {
        question: t("common:attention"),
        text: t("common:delete_confirm"),
        acceptTitle: t("common:delete"),
        onAccept: async () => {
          rowDelete.mutate({
            employeeId: row.employee.id,
            tableId: table.id,
            rowId: row.id,
          });
        },
      });
    },
    [table, canManageCustomTableForEmployee, openModal, rowDelete, t]
  );

  const onApplySorting = useCallback(
    (sorting: { fields: { field: string; direction: ApiSortingDirection }[] }) => {
      setSearchParams((current) => {
        if (sorting.fields.length === 0) {
          current.delete("sorting");
        } else {
          current.set("sorting", sorting.fields.map((s) => `${s.field}:${s.direction}`).join(","));
        }
        return current;
      });
    },
    [setSearchParams]
  );

  const handleOpenFilter = useCallback(() => {
    if (!table) return;
    openModal(EmployeeCustomTableRowsFilterModal, {
      filter,
      isHistoricalTable: table.is_historical ?? false,
      tableFields: table.table_fields,
    });
  }, [openModal, filter, table]);

  const handleExportClick = useCallback(
    (withHistory: boolean = false) => {
      if (!table) return;
      const filterToExport = {
        ...filter,
        with_history: withHistory,
        format: "xlsx",
      };
      downloadFile({
        apiCall: () => apiCustomFields.exportTableRows(table.id, filterToExport),
      });
    },
    [table, filter, downloadFile]
  );

  const { data, isPending } = useTableRows(table?.id ?? 0, filter, !!table);

  const hasAnyManageable = useMemo(() => {
    if (!table) return false;
    return (data?.items ?? []).some((row) =>
      canManageCustomTableForEmployee((row as ApiCFEmployeeTableRow).employee, table.id)
    );
  }, [data, table, canManageCustomTableForEmployee]);

  const handleAddRecord = () => {
    if (!table) return;
    openModal(EmployeeCustomTableRecordModal, {
      table,
    });
  };

  const isHistoricalTable = table?.is_historical ?? false;

  const columnHelper = createColumnHelper<ApiCFEmployeeTableRow>();
  const columns = useMemo<ColumnDef<ApiCFEmployeeTableRow>[]>(() => {
    if (!table) return [];

    const inner: ColumnDef<ApiCFEmployeeTableRow, unknown>[] = [];

    inner.push(
      columnHelper.accessor((row) => row.employee, {
        id: CFTableRowSortColumn.employee,
        header: t("core:full_name"),
        cell: (cell) => (
          <EmployeeItem
            employee={cell.row.original.employee}
            showPosition={true}
            size={"md"}
            enableLink={true}
            target={"_self"}
          />
        ),
        ...ColumnSizes.employee,
        enableSorting: true,
        sortDescFirst: false,
      })
    );

    if (isHistoricalTable) {
      inner.push(
        columnHelper.accessor((row) => row.date_effective_from, {
          id: CFTableRowSortColumn.effective_from,
          header: t("common:date"),
          cell: (cell) => {
            const row = cell.row.original;
            return row.date_effective_from ? renderDate(row.date_effective_from) : "-";
          },
          enableSorting: true,
          sortDescFirst: false,
        })
      );
    }

    const sortableFieldTypes = new Set<ApiFieldValueTypes>([
      ApiFieldValueTypes.text,
      ApiFieldValueTypes.textarea,
      ApiFieldValueTypes.email,
      ApiFieldValueTypes.phone,
      ApiFieldValueTypes.url,
      ApiFieldValueTypes.currency,
      ApiFieldValueTypes.number,
      ApiFieldValueTypes.percentage,
      ApiFieldValueTypes.date,
      ApiFieldValueTypes.datetime,
      ApiFieldValueTypes.switch,
      ApiFieldValueTypes.select,
      ApiFieldValueTypes.employee_reference,
    ]);

    // effective_from has its own sortable column above; changed_by is sortable only through this loop.
    const canViewChangedBy = (data?.items ?? []).some((row) =>
      canViewCustomTableChangedByForEmployee((row as ApiCFEmployeeTableRow).employee, table.id)
    );

    table.table_fields
      .filter((field) => field.type !== ApiFieldTypes.effective_from)
      .filter((field) => field.type !== ApiFieldTypes.changed_by || canViewChangedBy)
      .forEach((field) => {
        const isChangedBy = field.type === ApiFieldTypes.changed_by;

        inner.push(
          columnHelper.accessor(
            (row) =>
              isChangedBy ? row.changed_by : row.cells.find((c) => c.field.slug === field.slug),
            {
              id: field.slug,
              header:
                isChangedBy || isCompensationRollupField(field.type)
                  ? getFieldTitle(field)
                  : field.title,
              enableSorting: sortableFieldTypes.has(field.field_type),
              sortDescFirst: false,
              cell: (cell) => {
                const row = cell.row.original;

                if (isChangedBy) {
                  return row.changed_by ? (
                    <EmployeeHoverCard
                      employee={row.changed_by}
                      position={getEmployeePositionTitle(row.changed_by)}
                    >
                      <EmployeeItem employee={row.changed_by} showPosition={false} size="sm" />
                    </EmployeeHoverCard>
                  ) : null;
                }

                const tableCell = row.cells.find((c) => c.field.slug === field.slug);
                if (!tableCell && !isCompensationRollupField(field.type)) return "-";

                const customFields = row.cells.map(
                  (rowCell) => ({ field: rowCell.field, value: rowCell.value }) as ApiCustomField
                );

                if (field.field_type === ApiFieldValueTypes.textarea) {
                  return (
                    <ExpandableText className="whitespace-pre-wrap">
                      {renderFieldDataInCell(field, customFields)}
                    </ExpandableText>
                  );
                }

                if (field.field_type === ApiFieldValueTypes.currency) {
                  return renderFieldDataInCell(field, customFields);
                }

                return <span>{renderFieldDataInCell(field, customFields)}</span>;
              },
              ...(field.field_type === ApiFieldValueTypes.textarea ? ColumnSizes.wide : {}),
            }
          )
        );
      });

    inner.push(
      columnHelper.display({
        id: "actions",
        cell: (cell) => {
          const canManage = canManageCustomTableForEmployee(cell.row.original.employee, table.id);
          if (!canManage) return null;
          return (
            <Stack className="items-end">
              <DropdownMenu>
                <DropdownMenuItem
                  onClick={() => handleEdit(cell.row.original)}
                  title={t("common:edit")}
                />
                <DropdownMenuItem
                  onClick={() => handleDeleteAction(cell.row.original)}
                  variant="menuDanger"
                  title={t("common:delete") + "..."}
                />
              </DropdownMenu>
            </Stack>
          );
        },
        ...ColumnSizes.actions,
      })
    );

    return inner;
  }, [
    table,
    isHistoricalTable,
    renderFieldDataInCell,
    t,
    getFieldTitle,
    columnHelper,
    data,
    canViewCustomTableChangedByForEmployee,
  ]);

  if (!table) return <NotFound />;

  return (
    <Page className="h-full">
      <Page.Header title={table?.title ?? ""} showBack={true}>
        <Stack direction="horizontal" gap="sm">
          {hasAnyManageable && (
            <Button
              variant={"secondary"}
              onClick={handleAddRecord}
              leftIcon={<FontAwesomeIcon icon="fa-light fa-plus" />}
            >
              {t("common:add_record")}
            </Button>
          )}
          <Button
            variant={activeFilterCount === 0 ? "secondary" : "primary"}
            leftIcon={<FontAwesomeIcon icon="fa-light fa-bars-filter" />}
            rightIcon={
              activeFilterCount > 0 ? (
                <NotificationCount count={activeFilterCount} size={"sm"} />
              ) : undefined
            }
            onClick={handleOpenFilter}
          >
            {t("common:filter")}
          </Button>
          {canIEdit(ApiPermission.reporting_export) &&
            (isHistoricalTable ? (
              <SplitButton
                onClick={() => handleExportClick(false)}
                menuLabel={t("common:more_export_options")}
                menu={
                  <DropdownMenuItem
                    onClick={() => handleExportClick(true)}
                    title={t("custom_fields:export_with_history")}
                  />
                }
              >
                {t("common:export")}
              </SplitButton>
            ) : (
              <Button onClick={() => handleExportClick(false)}>{t("common:export")}</Button>
            ))}
        </Stack>
      </Page.Header>
      <Page.Content stretch>
        <Page.StickyLayout
          className="px-16"
          toolbar={
            <Stack className="px-16">
              <Search
                onInput={(e) => {
                  setSearchTerm(e.currentTarget.value);
                }}
                type={"text"}
                value={searchTerm}
                placeholder={t("common:search")}
                clearable={true}
                onClear={() => {
                  setSearchTerm(undefined);
                }}
              />
            </Stack>
          }
          footer={
            data ? (
              <Stack className="px-16">
                <PaginationBlock
                  metadata={data.metadata}
                  onPageChange={(page) => {
                    setSearchParams((current) => {
                      current.set("page", page.toString());
                      return current;
                    });
                  }}
                />
              </Stack>
            ) : undefined
          }
        >
          <Table<ApiCFEmployeeTableRow>
            data={(data?.items ?? []) as ApiCFEmployeeTableRow[]}
            columns={columns}
            stickyHeader={true}
            stickyFirstColumn={true}
            showSkeleton={isPending}
            sorting={filter.sorting}
            onSortUpdated={onApplySorting}
            stickyLastColumn={hasAnyManageable}
          />
        </Page.StickyLayout>
      </Page.Content>
    </Page>
  );
}
